import React, {FC} from "react"
import styled, {css} from "styled-components"

import Flex from "@irati/ui/src/Flex"
import {useTheme} from "@irati/ui/src/global/useTheme"
import {SignUpBillingPeriod, SignUpQuoteOption} from "@irati/types"
import {Card} from "@irati/ui/src/Layout"
import FormattedMessage from "@irati/utils/lang"
import {Body, H1, H3, Label} from "@irati/ui/src/Typography"
import {formatCurrency} from "@irati/utils/currency/formatCurrency"
import {DesktopOnlyWrapper, MobileOnlyWrapper, StyledFlex} from "./styled"

const SignUpQuoteCard = styled(Card)`
  max-width: 595px;
  position: relative;
  cursor: pointer;
  ${({theme, isSelected}) => isSelected && `background-color: ${theme.colors.secondary1_50}`};
`

const SavingPill = styled(Flex)`
  width: 125px;
  height: 30px;
  position: absolute;
  top: -8px;
  right: 0;
  background-color: ${({theme}) => theme.colors.utility.promo};
  border-radius: 4px;
  ${({theme}) => css`
    ${theme.mediaQueries.maxWidthMedia.desktop`
      top: -16px;
    `}
  `}
`

interface Props {
  isAnnual: boolean
  quote: SignUpQuoteOption
  value: number
  onClick: () => void
  employees: number
  saving: string
}

const SignUpQuote: FC<Props> = ({isAnnual, quote, value, onClick, employees, saving}) => {
  const theme = useTheme()

  return (
    <SignUpQuoteCard onClick={onClick} isSelected={value}>
      {isAnnual && saving && (
        <SavingPill justifyContent="center" alignItems="center">
          <Body large>
            🤑 <FormattedMessage id="platform.selfSignUp.confirmPackage.save" values={{saving}} />
          </Body>
        </SavingPill>
      )}
      <Flex alignItems="center" gap={2}>
        <Flex justifyContent="space-between" width="100%">
          <div>
            <H3 color={theme.colors.primary2}>
              {isAnnual ? (
                <FormattedMessage id="platform.selfSignUp.confirmPackage.annual" />
              ) : (
                <FormattedMessage id="platform.selfSignUp.confirmPackage.monthly" />
              )}
            </H3>
            <Body color={theme.colors.primary2}>{formatCurrency({value: quote.pricePerMonth * 12 * employees})}/yr</Body>

            <Body color={theme.colors.greys.grey50}>
              {quote.billingPeriod === SignUpBillingPeriod.BillingPeriodQuarterly ? (
                <FormattedMessage
                  id="platform.selfSignUp.confirmPackage.billedAs"
                  values={{value: formatCurrency({value: quote.pricePerMonth * 3 * employees})}}
                />
              ) : (
                <FormattedMessage
                  id="platform.selfSignUp.confirmPackage.billedMonthly"
                  values={{value: formatCurrency({value: quote.pricePerMonth * employees})}}
                />
              )}
            </Body>
          </div>
          <StyledFlex>
            <DesktopOnlyWrapper>
              <Flex height="100%" alignItems="center">
                <H1 color={theme.colors.primary2}>{formatCurrency({value: quote.pricePerMonth})}</H1>
                <Label padding={[1, 0, 0, 1]}>
                  <FormattedMessage id="platform.selfSignUp.confirmPackage.billedLabel" />
                </Label>
              </Flex>
            </DesktopOnlyWrapper>
            <MobileOnlyWrapper>
              <Flex direction="column" marginTop={1}>
                <H1 color={theme.colors.primary2}>{formatCurrency({value: quote.pricePerMonth})}</H1>
                <Label>
                  <FormattedMessage id="platform.selfSignUp.confirmPackage.billedLabel" />
                </Label>
              </Flex>
            </MobileOnlyWrapper>
          </StyledFlex>
        </Flex>
      </Flex>
    </SignUpQuoteCard>
  )
}

export default SignUpQuote
